import { Capacitor } from '@capacitor/core';
import { Downloads } from '@/plugins/downloads';

/**
 * Android-native PDF opening.
 *
 * On Android we stream the remote (signed) URL straight to the app cache via the native
 * `Downloads` plugin and hand it to the device's built-in PDF viewer (FileProvider + ACTION_VIEW).
 * This avoids pulling the whole file into the JS heap as base64, so large PDFs stay memory-safe.
 *
 * Web/iOS keep using the existing in-app modal, and any native failure falls back to that modal.
 */

export type OpenPdfFailureReason =
  | 'not-android'
  | 'mock-url'
  | 'no-viewer'
  | 'download-failed';

export interface OpenPdfNativeResult {
  opened: boolean;
  reason?: OpenPdfFailureReason;
  error?: unknown;
}

/** Cheap synchronous guard: only attempt the native viewer for real URLs on Android. */
export function canOpenPdfNatively(url: string | null | undefined): boolean {
  return Capacitor.getPlatform() === 'android' && !!url && url !== '#mock-url';
}

/**
 * Open a remote PDF in the Android native viewer. Never throws — inspect `result.opened`.
 * Callers fall back to the in-app modal whenever `opened` is false.
 */
export async function openPdfNative(input: {
  url: string;
  fileName?: string;
}): Promise<OpenPdfNativeResult> {
  if (Capacitor.getPlatform() !== 'android') {
    return { opened: false, reason: 'not-android' };
  }
  if (input.url === '#mock-url') {
    return { opened: false, reason: 'mock-url' };
  }

  try {
    const res = await Downloads.openRemotePdf({ url: input.url, fileName: input.fileName });
    return res.opened
      ? { opened: true }
      : { opened: false, reason: 'download-failed' };
  } catch (error) {
    const message = String((error as { message?: string })?.message ?? error);
    const reason: OpenPdfFailureReason = message.includes('no-viewer')
      ? 'no-viewer'
      : 'download-failed';
    return { opened: false, reason, error };
  }
}
